// src/pages/Dashboard/Agencies/DashEditAgency.jsx
import React, { useEffect, useState } from "react";
import { useUtil } from "../../../contexts/UtilContext";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import styles from "./DashEditAgency.module.css";
import {
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../contexts/AuthContext";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { stateOptions } from "../../../util/formElements";
import LanguageSelector from "../../../components/LanguageSelector";
import { toast } from "react-toastify";

const specializations = [
  "Residential",
  "Commercial",
  "Investment",
  "Specialty Properties",
  "Real Estate Development",
  "Real Estate Management",
  "Distressed Properties",
  "Real Estate Law and Consulting",
  "Green and Sustainable Real Estate",
  "International",
];

export default function DashEditAgency() {
  const { id } = useParams(); // /agency/:id/edit
  const { accessToken } = useAuth();
  const { showLoader, hideLoader } = useUtil();
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState(""); // Store uploaded logo URL

  // keep same shape as Add page
  const [step, setStep] = useState(2); // start on Basic
  const [emailDetails, setEmailDetails] = useState({ email: "" });
  const [basicDetails, setBasicDetails] = useState({
    name: "",
    bio: "",
    phone: "",
    licenseNumber: "",
  });
  const [addressDetails, setAddressDetails] = useState({
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipcode: "",
    landmark: "",
  });
  const [professionalDetails, setProfessionalDetails] = useState({
    establishedYear: null,
    languages: "",
    specialization: "",
    linkedinLink: "",
    instagramLink: "",
    youtubeLink: "",
    tiktokLink: "",
  });
  const [logoPreview, setLogoPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateLogo(file)) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const validateLogo = (file) => {
    const maxSizeMB = 5;
    const okTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Logo must be PNG, JPG, or WEBP.");
      return false;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Logo must be less than ${maxSizeMB}MB.`);
      return false;
    }
    return true;
  };

  const uploadLogo = async (agencyId) => {
    if (!logoFile) return null;

    const form = new FormData();
    form.append("logo", logoFile); // must match backend upload.single("logo")
    form.append("agencyId", agencyId);

    const res = await axios.post(
      `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/upload-logo`,
      form,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!res.data?.success || !res.data?.url) {
      throw new Error(res.data?.message || "Logo upload failed");
    }

    setLogoUrl(res.data.url); // Store uploaded logo URL
    return res.data.url;
  };

  // ---------- fetch existing agency ----------
  useEffect(() => {
    const run = async () => {
      try {
        showLoader();
        const res = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/${id}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!res.data?.success) throw new Error("Failed to load agency.");

        const a = res.data.agency;

        setEmailDetails({ email: a.email || "" });
        setBasicDetails({
          name: a.agency_name || "",
          bio: a.biography || "",
          phone: a.phone || "",
          licenseNumber: a.license_number || "",
          logo_url: a.logo_url || "",
        });
        setAddressDetails({
          street1: a.street || "",
          street2: a.street_2 || "",
          city: a.city || "",
          state: a.state || "",
          zipcode: a.zipcode || "",
          landmark: a.landmark || "",
        });
        setProfessionalDetails({
          establishedYear: a.established_year
            ? dayjs(a.established_year)
            : null,
          languages: a.languages || "",
          specialization: a.specialization || "",
          linkedinLink: a.linkedin_link || "",
          instagramLink: a.instagram_link || "",
          youtubeLink: a.youtube_link || "",
          tiktokLink: a.tiktok_link || "",
        });
      } catch (e) {
        console.error(e);
        toast.error("Could not load agency.");
        navigate("/MyFirstLand?category=agencies");
      } finally {
        hideLoader();
      }
    };
    if (id && accessToken) run();
  }, [id, accessToken]);

  // ---------- handlers (same as Add) ----------
  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setBasicDetails((p) => ({ ...p, [name]: value }));
  };
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressDetails((p) => ({ ...p, [name]: value }));
  };
  const handleEstablishedYearChange = (date) => {
    setProfessionalDetails((p) => ({
      ...p,
      establishedYear: date ? dayjs(date) : null,
    }));
  };
  const handleProfessionalChange = (e) => {
    const { name, value } = e.target;
    if (name === "specialization" || name === "languages") {
      const str = Array.isArray(value) ? value.join(", ") : value;
      setProfessionalDetails((p) => ({ ...p, [name]: str }));
    } else {
      setProfessionalDetails((p) => ({ ...p, [name]: value }));
    }
  };

  // ---------- validation (reuse your rules) ----------
  const validateBasic = () => {
    if (!basicDetails.name)
      return toast.error("Agency name is required."), false;
    if (!basicDetails.phone)
      return toast.error("Phone number is required."), false;
    if (!basicDetails.licenseNumber)
      return toast.error("License number is required."), false;
    return true;
  };
  const validateAddress = () => {
    const a = addressDetails;
    if (!a.street1) return toast.error("Street address is required."), false;
    if (!a.city) return toast.error("City is required."), false;
    if (!a.state) return toast.error("State is required."), false;
    if (!a.zipcode) return toast.error("Zipcode is required."), false;
    if (!a.landmark) return toast.error("Landmark is required."), false;
    return true;
  };
  const validateProfessional = () => {
    const p = professionalDetails;
    if (!p.languages) return toast.error("Languages are required."), false;
    if (!p.establishedYear)
      return toast.error("Established year is required."), false;
    if (!p.specialization)
      return toast.error("Specialization is required."), false;
    return true;
  };

  // ---------- navigation between steps ----------
  const handleNext = () => {
    if (step === 2 && !validateBasic()) return;
    if (step === 3 && !validateAddress()) return;
    if (step === 4 && !validateProfessional()) return;
    setStep((s) => Math.min(4, s + 1));
  };
  const handleBack = () => setStep((s) => Math.max(2, s - 1));

  // ---------- submit (PATCH) ----------
  const handleSubmit = async () => {
    if (!validateProfessional()) return;
    try {
      showLoader();
      const payload = {
        emailDetails,
        basicDetails,
        addressDetails,
        professionalDetails: {
          ...professionalDetails,
          establishedYear: professionalDetails.establishedYear
            ? professionalDetails.establishedYear.toISOString()
            : null,
        },
      };
      await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/${id}`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toast.success("Agency updated.");
      if (logoFile) {
        await uploadLogo(id);
      }
      navigate("/MyFirstLand?category=agencies");
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Update failed.");
    } finally {
      hideLoader();
    }
  };

  return (
    <div className={styles.wrapper}>
      <Navbar />
      <div className={styles.container}>
        <h2 className={styles.title}>Edit Agency</h2>

        {/* Progress (skip email + verification steps) */}
        <div className={styles.progressBar}>
          <div
            className={`${styles.step} ${step >= 2 ? styles.active : ""}`}
          ></div>
          <div
            className={`${styles.step} ${step >= 3 ? styles.active : ""}`}
          ></div>
          <div
            className={`${styles.step} ${step >= 4 ? styles.active : ""}`}
          ></div>
        </div>

        <div className={styles.formContainer}>
          {/* step 2: Basic */}
          {step === 2 && (
            <div className={styles.formSection}>
              <div className={styles.formComponent}>
                <div className={styles.logoRow}>
                  <div className={styles.logoPreviewWrap}>
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className={styles.logoPreview}
                      />
                    ) : basicDetails.logo_url ? (
                      <img
                        src={basicDetails.logo_url}
                        alt="Logo preview"
                        className={styles.logoPreview}
                      />
                    ) : (
                      <div className={styles.logoPlaceholder}>Logo</div>
                    )}
                  </div>

                  <div className={styles.logoActions}>
                    <input
                      id="logo-input"
                      type="file"
                      accept="image/png, image/jpeg, image/webp"
                      style={{ display: "none" }}
                      onChange={onLogoChange}
                    />
                    <Button
                      variant="outlined"
                      onClick={() =>
                        document.getElementById("logo-input").click()
                      }
                    >
                      {logoFile ? "Change Logo" : "Upload Logo"}
                    </Button>
                    {logoFile && (
                      <Button
                        variant="text"
                        onClick={() => {
                          setLogoFile(null);
                          setLogoPreview("");
                        }}
                        className={styles.textButton}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.formComponent}>
                <TextField
                  label="Agency Email"
                  value={emailDetails.email}
                  fullWidth
                  disabled
                />
              </div>
              <div className={styles.formComponent}>
                <TextField
                  id="name"
                  name="name"
                  label="Name of the Agency"
                  variant="outlined"
                  required
                  fullWidth
                  value={basicDetails.name}
                  onChange={handleBasicChange}
                />
              </div>
              <div
                className={`${styles.formComponent} ${styles.componentType1}`}
              >
                <TextField
                  id="phone"
                  name="phone"
                  label="Phone Number"
                  variant="outlined"
                  type="tel"
                  fullWidth
                  value={basicDetails.phone}
                  onChange={handleBasicChange}
                />
                <TextField
                  id="licenseNumber"
                  name="licenseNumber"
                  label="Agency License Number"
                  variant="outlined"
                  required
                  fullWidth
                  value={basicDetails.licenseNumber}
                  onChange={handleBasicChange}
                />
              </div>
              <div className={styles.formComponent}>
                <TextField
                  id="bio"
                  name="bio"
                  label="Bio"
                  multiline
                  rows={4}
                  value={basicDetails.bio}
                  onChange={handleBasicChange}
                />
              </div>
              <div className={styles.formComponent}>
                <div className={styles.formActions}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button variant="contained" size="large" onClick={handleNext}>
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* step 3: Address */}
          {step === 3 && (
            <div className={styles.formSection}>
              <div className={styles.formSection}>
                <TextField
                  id="street1"
                  name="street1"
                  label="Street Address 1"
                  required
                  fullWidth
                  value={addressDetails.street1}
                  onChange={handleAddressChange}
                />
              </div>
              <div
                className={`${styles.formComponent} ${styles.componentType2}`}
              >
                <TextField
                  id="street2"
                  name="street2"
                  label="Street Address 2"
                  fullWidth
                  value={addressDetails.street2}
                  onChange={handleAddressChange}
                />
                <TextField
                  id="city"
                  name="city"
                  label="City"
                  required
                  fullWidth
                  value={addressDetails.city}
                  onChange={handleAddressChange}
                />
              </div>
              <div
                className={`${styles.formComponent} ${styles.componentType3}`}
              >
                <FormControl fullWidth>
                  <InputLabel id="state-label">State *</InputLabel>
                  <Select
                    labelId="state-label"
                    id="state"
                    name="state"
                    label="State"
                    value={addressDetails.state}
                    onChange={handleAddressChange}
                  >
                    {stateOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  id="zipcode"
                  name="zipcode"
                  label="Zipcode"
                  required
                  fullWidth
                  value={addressDetails.zipcode}
                  onChange={handleAddressChange}
                />
                <TextField
                  id="landmark"
                  name="landmark"
                  label="Landmark Nearby"
                  required
                  fullWidth
                  value={addressDetails.landmark}
                  onChange={handleAddressChange}
                />
              </div>
              <div className={styles.formComponent}>
                <div className={styles.formActions}>
                  <Button variant="outlined" size="large" onClick={handleBack}>
                    Back
                  </Button>
                  <Button variant="contained" size="large" onClick={handleNext}>
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* step 4: Professional */}
          {step === 4 && (
            <div className={styles.formSection}>
              <div
                className={`${styles.formComponent} ${styles.componentType2}`}
              >
                <LanguageSelector
                  professionalDetails={professionalDetails}
                  handleProfessionalChange={handleProfessionalChange}
                />
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      id="establishedYear"
                      label="Established Year"
                      views={["year"]}
                      maxDate={dayjs()}
                      value={professionalDetails.establishedYear || null}
                      onChange={handleEstablishedYearChange}
                    />
                  </LocalizationProvider>
                </FormControl>
              </div>
              <div
                className={`${styles.formComponent} ${styles.componentType2}`}
              >
                <FormControl fullWidth required>
                  <InputLabel id="specialization-label">
                    Specialization
                  </InputLabel>
                  <Select
                    labelId="specialization-label"
                    id="specialization"
                    name="specialization"
                    multiple
                    value={professionalDetails.specialization
                      .split(", ")
                      .filter(Boolean)}
                    onChange={handleProfessionalChange}
                    renderValue={(selected) => (
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                      >
                        {selected.map((v) => (
                          <Chip key={v} label={v} />
                        ))}
                      </div>
                    )}
                  >
                    {specializations.map((spec) => (
                      <MenuItem key={spec} value={spec}>
                        {spec}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <h3 className="mar-t-40">Public Social Links:</h3>
              <div
                className={`${styles.formComponent} ${styles.componentType1}`}
              >
                <TextField
                  id="linkedinLink"
                  name="linkedinLink"
                  label="LinkedIn Link"
                  fullWidth
                  value={professionalDetails.linkedinLink}
                  onChange={handleProfessionalChange}
                />
                <TextField
                  id="instagramLink"
                  name="instagramLink"
                  label="Instagram Link"
                  fullWidth
                  value={professionalDetails.instagramLink}
                  onChange={handleProfessionalChange}
                />
                <TextField
                  id="youtubeLink"
                  name="youtubeLink"
                  label="YouTube Link"
                  fullWidth
                  value={professionalDetails.youtubeLink}
                  onChange={handleProfessionalChange}
                />
                <TextField
                  id="tiktokLink"
                  name="tiktokLink"
                  label="Tiktok Link"
                  fullWidth
                  value={professionalDetails.tiktokLink}
                  onChange={handleProfessionalChange}
                />
              </div>
              <div className={styles.formComponent}>
                <div className={styles.formActions}>
                  <Button variant="outlined" size="large" onClick={handleBack}>
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
